import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { IndianRupee, Plus, X, Users } from 'lucide-react';

import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

const EMPTY_PAYER = { user_id: '', amount: '' };

// Converts a legacy or new-format cost_breakdown category into a payers array
function loadPayers(categoryData) {
  if (!categoryData) return [{ ...EMPTY_PAYER }];
  if (categoryData.payers?.length > 0) {
    return categoryData.payers.map(p => ({ user_id: p.user_id || '', amount: p.amount.toString() }));
  }
  // Legacy single-payer format
  if (categoryData.payer_id) {
    return [{ user_id: categoryData.payer_id, amount: (categoryData.amount || '').toString() }];
  }
  return [{ ...EMPTY_PAYER }];
}

const sumPayers = (payers) => payers.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

export function CostBreakdownModal({ match, isOpen, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimActionLoading, setClaimActionLoading] = useState(false);

  // Cost breakdown state - each category is a list of { user_id, amount } payers
  const [groundPayers, setGroundPayers] = useState([{ ...EMPTY_PAYER }]);
  const [breakfastPayers, setBreakfastPayers] = useState([{ ...EMPTY_PAYER }]);
  const [miscPayers, setMiscPayers] = useState([{ ...EMPTY_PAYER }]);

  useEffect(() => {
    if (isOpen && match) {
      fetchPlayers();
      if (match.match_type === 'sunday') {
        fetchClaims();
      }
      // Pre-fill if cost breakdown exists
      if (match.cost_breakdown) {
        const cb = match.cost_breakdown;
        setGroundPayers(loadPayers(cb.ground));
        setBreakfastPayers(loadPayers(cb.breakfast));
        setMiscPayers(loadPayers(cb.misc));
      } else {
        setGroundPayers([{ ...EMPTY_PAYER }]);
        setBreakfastPayers([{ ...EMPTY_PAYER }]);
        setMiscPayers([{ ...EMPTY_PAYER }]);
      }
    }
  }, [isOpen, match]);

  const fetchClaims = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/matches/${match.match_id}/breakfast-claims`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setClaims(res.data);
    } catch (error) {
      console.error('Failed to fetch breakfast claims');
    }
  };

  const handleVerifyClaim = async (claim, approved) => {
    setClaimActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/matches/${match.match_id}/breakfast-claims/${claim.claim_id}/verify`,
        { approved },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );

      if (approved) {
        setBreakfastPayers(prev => {
          const existingIndex = prev.findIndex(p => p.user_id === claim.user_id);
          if (existingIndex >= 0) {
            return prev.map((p, i) => i === existingIndex ? { user_id: claim.user_id, amount: claim.amount.toString() } : p);
          }
          const emptyIndex = prev.findIndex(p => !p.user_id && !p.amount);
          if (emptyIndex >= 0) {
            return prev.map((p, i) => i === emptyIndex ? { user_id: claim.user_id, amount: claim.amount.toString() } : p);
          }
          return [...prev, { user_id: claim.user_id, amount: claim.amount.toString() }];
        });
        toast.success(`Verified ₹${claim.amount} and added to breakdown`);
      } else {
        toast.success('Claim rejected');
      }

      setClaims(prev => prev.map(c => c.claim_id === claim.claim_id ? { ...c, status: approved ? 'approved' : 'rejected' } : c));
    } catch (error) {
      toast.error('Failed to verify claim');
    } finally {
      setClaimActionLoading(false);
    }
  };

  const handleVerifyAllPending = async () => {
    const pending = claims.filter(c => c.status === 'pending');
    for (const claim of pending) {
      await handleVerifyClaim(claim, true);
    }
  };

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/all`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });

      // Get current user info
      const meRes = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      const currentUserId = meRes.data.user_id;

      // Filter to selected players for this match
      const selectedIds = match.selected_players || [];
      const selectedPlayers = res.data.filter(u => selectedIds.includes(u.user_id));

      // Always include current admin user as a payer option, even if not playing
      const isCurrentUserIncluded = selectedPlayers.some(p => p.user_id === currentUserId);
      if (!isCurrentUserIncluded) {
        const currentUser = res.data.find(u => u.user_id === currentUserId);
        if (currentUser) {
          // Add current user at the beginning with a marker
          selectedPlayers.unshift({
            ...currentUser,
            _isExtraPayer: true // marker to show they're not playing
          });
        }
      }

      setPlayers(selectedPlayers);
    } catch (error) {
      console.error('Failed to fetch players');
    }
  };

  const groundCost = sumPayers(groundPayers);
  const breakfastCost = sumPayers(breakfastPayers);
  const miscCost = sumPayers(miscPayers);
  const totalCost = groundCost + breakfastCost + miscCost;
  const perPlayerShare = match.selected_players?.length > 0 ? totalCost / match.selected_players.length : 0;

  const handleSubmit = async () => {
    if (totalCost <= 0) {
      toast.error('Please enter at least one cost component');
      return;
    }

    setLoading(true);
    try {
      const toPayload = (payers) => payers
        .filter(p => parseFloat(p.amount) > 0)
        .map(p => ({ user_id: p.user_id || null, amount: parseFloat(p.amount) }));

      await axios.put(
        `${API_URL}/api/matches/${match.match_id}/set-cost-breakdown`,
        {
          ground_payers: toPayload(groundPayers),
          breakfast_payers: toPayload(breakfastPayers),
          misc_payers: toPayload(miscPayers)
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Cost breakdown saved! Player dues updated.');
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cost breakdown');
    } finally {
      setLoading(false);
    }
  };

  const getPlayerName = (userId) => {
    const player = players.find(p => p.user_id === userId);
    return player?.nickname || player?.name || 'Unknown';
  };

  const allContributors = [
    ...groundPayers.map(p => ({ ...p, category: 'ground' })),
    ...breakfastPayers.map(p => ({ ...p, category: 'breakfast' })),
    ...miscPayers.map(p => ({ ...p, category: 'misc' }))
  ].filter(p => p.user_id && parseFloat(p.amount) > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-[#E63946]" />
            Cost Breakdown
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500">Total Cost</p>
                <p className="text-2xl font-bold text-[#0F172A]" data-testid="total-cost-display">₹{totalCost.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Per Player</p>
                <p className="text-2xl font-bold text-[#E63946]">₹{perPlayerShare.toFixed(0)}</p>
              </div>
            </div>
            <p className="text-xs text-center text-slate-500 mt-2">
              {match.selected_players?.length || 0} players selected
            </p>
          </div>

          {/* Ground Cost */}
          <PayerSection
            title="Ground Cost"
            colorClass="bg-green-500"
            payers={groundPayers}
            setPayers={setGroundPayers}
            players={players}
            testIdPrefix="ground"
            subtotal={groundCost}
          />

          {/* Player-Submitted Breakfast Claims */}
          {match.match_type === 'sunday' && claims.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="breakfast-claims-section">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Player-Submitted Breakfast Costs
                </p>
                {claims.some(c => c.status === 'pending') && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={handleVerifyAllPending}
                    disabled={claimActionLoading}
                    className="text-xs text-green-700 hover:text-green-800 h-6"
                    data-testid="verify-all-claims-btn"
                  >
                    Verify &amp; Add All
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                {claims.map(claim => (
                  <div key={claim.claim_id} className="flex items-center justify-between bg-white rounded px-2 py-1.5 text-xs">
                    <span className="text-slate-700">
                      {claim.user?.nickname || claim.user?.name}: <span className="font-semibold">₹{claim.amount}</span>
                    </span>
                    {claim.status === 'pending' ? (
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleVerifyClaim(claim, true)}
                          disabled={claimActionLoading}
                          className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700"
                          data-testid={`verify-claim-approve-${claim.claim_id}`}
                        >
                          Verify &amp; Add
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleVerifyClaim(claim, false)}
                          disabled={claimActionLoading}
                          className="h-6 px-2 text-xs text-red-500"
                          data-testid={`verify-claim-reject-${claim.claim_id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : claim.status === 'approved' ? (
                      <span className="text-green-600 font-semibold">✓ Added</span>
                    ) : (
                      <span className="text-red-400">Rejected</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Breakfast Cost */}
          <PayerSection
            title="Breakfast Cost"
            colorClass="bg-orange-500"
            payers={breakfastPayers}
            setPayers={setBreakfastPayers}
            players={players}
            testIdPrefix="breakfast"
            subtotal={breakfastCost}
          />

          {/* Misc/Balls Cost */}
          <PayerSection
            title="Balls / Miscellaneous"
            colorClass="bg-blue-500"
            payers={miscPayers}
            setPayers={setMiscPayers}
            players={players}
            testIdPrefix="misc"
            subtotal={miscCost}
          />

          {/* Expense Contributors Preview */}
          {allContributors.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" />
                Expense Contributors
              </p>
              <div className="space-y-1 text-xs">
                {allContributors.map((p, i) => (
                  <p key={i} className="text-blue-700">
                    {getPlayerName(p.user_id)} paid ₹{parseFloat(p.amount).toFixed(0)} for {p.category}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading || totalCost <= 0}
            className="w-full bg-[#E63946] hover:bg-[#DC2626] text-white"
            data-testid="submit-cost-breakdown-btn"
          >
            {loading ? 'Saving...' : 'Save Cost Breakdown'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayerSection({ title, colorClass, payers, setPayers, players, testIdPrefix, subtotal }) {
  const addPayer = () => setPayers([...payers, { ...EMPTY_PAYER }]);

  const removePayer = (index) => {
    if (payers.length > 1) {
      setPayers(payers.filter((_, i) => i !== index));
    }
  };

  const updatePayer = (index, field, value) => {
    setPayers(payers.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${colorClass}`}></span>
          {title}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addPayer}
          className="text-xs text-blue-600 hover:text-blue-700"
          data-testid={`${testIdPrefix}-add-payer-btn`}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Payer
        </Button>
      </div>
      {payers.map((payer, index) => (
        <div key={index} className="flex gap-2 items-center">
          <Select
            value={payer.user_id}
            onValueChange={(v) => updatePayer(index, 'user_id', v)}
          >
            <SelectTrigger className="flex-1" data-testid={`${testIdPrefix}-payer-${index}-select`}>
              <SelectValue placeholder="Paid by" />
            </SelectTrigger>
            <SelectContent>
              {players.map(player => (
                <SelectItem key={player.user_id} value={player.user_id}>
                  {player.nickname || player.name}
                  {player._isExtraPayer && " (Admin)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            placeholder="Amount"
            className="w-24"
            value={payer.amount}
            onChange={(e) => updatePayer(index, 'amount', e.target.value)}
            data-testid={`${testIdPrefix}-payer-${index}-amount`}
          />
          {payers.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removePayer(index)}
              className="text-red-500 hover:text-red-600 h-8 w-8"
              data-testid={`${testIdPrefix}-remove-payer-${index}`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      <p className="text-xs text-slate-500 text-right" data-testid={`${testIdPrefix}-subtotal`}>
        Subtotal: ₹{subtotal.toFixed(0)}
      </p>
    </div>
  );
}

export default CostBreakdownModal;
